import { registerInvalidationHandler } from "@kontextwerk/web-sdk/core/runtime/invalidation"
import {
  fetchDistrictStatistics,
  type DistrictStatistics,
} from "../../../../../../../api/kehrblatt/district/statistics"
import {
  registerEventCallback,
  unregisterEventCallback,
} from "../../../../../../_shared/_logic/eventbus"
import {
  customerRegistryContactsInvalidationTarget,
  customerRegistryPropertiesInvalidationTarget,
} from "../../../_shared/_logic/customer-registry-invalidation"

export type DistrictStatisticsState = {
  organizationId: string
  statistics: DistrictStatistics | null
  loading: boolean
  error: string | null
}

export type DistrictStatisticsScope = {
  refresh: () => Promise<void>
  dispose: () => void
}

/** One organization owns its reads, coalesced invalidations and subscriptions. */
export const createDistrictStatisticsScope = (
  organizationId: string,
  ownerId: string,
  onState: (state: DistrictStatisticsState) => void,
): DistrictStatisticsScope => {
  let disposed = false
  let sequence = 0
  let controller: AbortController | null = null
  let scheduledRefresh: Promise<void> | null = null
  const load = async () => {
    if (disposed) return

    const request = ++sequence
    controller?.abort()
    const nextController = new AbortController()
    controller = nextController
    onState({ organizationId, statistics: null, loading: true, error: null })
    try {
      const statistics = await fetchDistrictStatistics(organizationId, nextController.signal)

      if (disposed || request !== sequence || nextController.signal.aborted) return

      onState({ organizationId, statistics, loading: false, error: null })
    } catch {
      if (disposed || request !== sequence || nextController.signal.aborted) return

      onState({
        organizationId,
        statistics: null,
        loading: false,
        error:
          "Die Kehrbezirksstatistik konnte nicht geladen werden. Bitte versuchen Sie es erneut.",
      })
    }
  }
  const refresh = () => {
    if (!scheduledRefresh) {
      scheduledRefresh = Promise.resolve().then(() => {
        scheduledRefresh = null

        return load()
      })
    }

    return scheduledRefresh
  }
  const unregisterContacts = registerInvalidationHandler(
    customerRegistryContactsInvalidationTarget(organizationId),
    refresh,
  )
  const unregisterProperties = registerInvalidationHandler(
    customerRegistryPropertiesInvalidationTarget(organizationId),
    refresh,
  )
  const eventId = `district-statistics-${ownerId}`
  registerEventCallback(
    "customer-registry.changed",
    eventId,
    (payload: { organizationId?: string; organization_id?: string } = {}) => {
      const changedOrganizationId = payload.organizationId ?? payload.organization_id

      if (changedOrganizationId && changedOrganizationId !== organizationId) return

      void refresh()
    },
  )
  void refresh()

  return {
    refresh,
    dispose: () => {
      if (disposed) return

      disposed = true
      controller?.abort()
      unregisterContacts()
      unregisterProperties()
      unregisterEventCallback("customer-registry.changed", eventId)
    },
  }
}
